import { cn } from '@spiaggeit/spit-ui'
import { useTranslation } from 'react-i18next'

import { Minus } from '../../assets/icons/Minus'
import { Plus } from '../../assets/icons/Plus'
import { IconButton } from '../IconButton'
import { Tooltip } from '../Tooltip/Tooltip'

interface Props {
  available: {
    global?: number
    group?: number
    ticket: number
  }
  hasLimit: boolean
  icon: string
  minimumQuantity: number
  name: string
  onAdd: VoidFunction
  onAddDisabled: VoidFunction
  onRemove: VoidFunction
  quantity: number
}

export function ItemQuantityController(props: Props) {
  const { t } = useTranslation()

  const global = props.available?.global ?? Infinity
  const group = props.available?.group ?? Infinity
  const ticket = props.available?.ticket ?? Infinity

  const availableQuantity = props.hasLimit
    ? Math.min(global, group, ticket - props.quantity)
    : Infinity

  const isSoldOut = props.hasLimit && props?.available.ticket === 0

  const isAddDisabled = availableQuantity < 1
  const isRemoveDisabled =
    props.quantity === 0 || props.quantity <= props.minimumQuantity

  function handleRemove() {
    if (!isRemoveDisabled) {
      props.onRemove()
    }
  }

  function handleAdd() {
    if (!isAddDisabled) {
      props.onAdd()
    }
  }

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 first:rounded-t last:rounded-b',
        {
          'cursor-not-allowed opacity-50': isSoldOut,
        }
      )}
    >
      <div className="flex flex-auto items-center gap-3">
        <div className="flex-initial shrink-0">
          <img
            alt={props.name}
            className="size-8"
            src={`/icons/${props.icon}`}
          />
        </div>

        <div className="flex-auto">
          <Tooltip
            className="font-medium"
            content={props.name}
            maxLines={3}
            overflowStyle="underline"
          />
        </div>
      </div>

      <div className="flex-0 relative flex items-center">
        <IconButton
          ariaLabel={t('itemQuantityController.remove')}
          icon={Minus}
          isDisabled={isRemoveDisabled}
          onClick={handleRemove}
        />

        <div className="flex size-10 items-center justify-center text-sm font-bold">
          {props.quantity}
        </div>

        <IconButton
          ariaLabel={t('itemQuantityController.add')}
          icon={Plus}
          isDisabled={isAddDisabled}
          onClick={handleAdd}
        />

        {isAddDisabled ? (
          <button
            className="absolute right-0 top-0 size-10"
            onClick={props.onAddDisabled}
          />
        ) : null}
      </div>
    </div>
  )
}
